import { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Import all showcase images
import chairGoals from "@/assets/showcase/chair-goals.png";
import dashboardOverview from "@/assets/showcase/dashboard-overview.png";
import assetsCatalogue from "@/assets/showcase/assets-catalogue.png";
import cateringStock from "@/assets/showcase/catering-stock.png";
import cateringMenu from "@/assets/showcase/catering-menu.png";
import communityLetters from "@/assets/showcase/community-letters.png";
import ptaAdminSummary from "@/assets/showcase/pta-admin-summary.png";
import ptaMembers from "@/assets/showcase/pta-members.png";
import integrations from "@/assets/showcase/integrations.png";
import fundraisingLibrary from "@/assets/showcase/fundraising-library.png";
import logoFull from "@/assets/logo-full-new.png";

interface Slide {
  image: string;
  headline: string;
  subtext: string;
  dashboardTitle?: string;
  isLogo?: boolean;
}

const slides: Slide[] = [
  {
    image: dashboardOverview,
    headline: "Your PTA Command Centre",
    subtext: "Track fundraising progress, upcoming events, and member activity at a glance",
    dashboardTitle: "PTA Dashboard",
  },
  {
    image: chairGoals,
    headline: "Manage Fundraising Goals",
    subtext: "Set targets, track progress, and prioritise goals with ease",
    dashboardTitle: "Chair Dashboard",
  },
  {
    image: ptaAdminSummary,
    headline: "Quick Setup & Onboarding",
    subtext: "Get your PTA up and running with our guided checklist",
    dashboardTitle: "PTA Admin",
  },
  {
    image: ptaMembers,
    headline: "Organise Your Team",
    subtext: "Invite members, assign roles, and manage permissions effortlessly",
    dashboardTitle: "Member Management",
  },
  {
    image: cateringStock,
    headline: "Stock Management Made Simple",
    subtext: "Track inventory, batches, and suppliers in one place",
    dashboardTitle: "Catering Dashboard",
  },
  {
    image: cateringMenu,
    headline: "Menu & Pricing Control",
    subtext: "Manage menu items, prices, and allergen information",
    dashboardTitle: "Catering Dashboard",
  },
  {
    image: assetsCatalogue,
    headline: "Track PTA Equipment",
    subtext: "Catalogue assets, manage loans, and track availability",
    dashboardTitle: "Assets Dashboard",
  },
  {
    image: communityLetters,
    headline: "AI-Powered Letter Builder",
    subtext: "Create professional letters for donations, sponsors, and volunteers",
    dashboardTitle: "Community Dashboard",
  },
  {
    image: integrations,
    headline: "Connect Your Tools",
    subtext: "Sync with Google Calendar, Meet, and Drive seamlessly",
    dashboardTitle: "Integrations",
  },
  {
    image: fundraisingLibrary,
    headline: "Proven Fundraising Ideas",
    subtext: "Browse seasonal events with income estimates and helper requirements",
    dashboardTitle: "Fundraising Library",
  },
  {
    image: logoFull,
    headline: "Launching Soon",
    subtext: "We're putting the finishing touches on something special",
    isLogo: true,
  },
];

const SLIDE_DURATION = 3500; // ~38.5s total for 11 slides

const MaintenanceShowcase = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio and autoplay
  useEffect(() => {
    const audio = new Audio();
    audio.src = "https://cdn.pixabay.com/download/audio/2022/03/15/audio_8cb749d484.mp3?filename=inspiring-cinematic-ambient-116199.mp3";
    audio.loop = true;
    audio.volume = 0.3;
    audioRef.current = audio;
    
    // Autoplay music
    audio.play().catch(() => {
      // Autoplay blocked by browser, user will need to click unmute
      setIsMuted(true);
    });

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Handle mute toggle
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.play().catch(() => {
          // Autoplay blocked
        });
      } else {
        audioRef.current.pause();
      }
      setIsMuted(!isMuted);
    }
  };

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
        setProgress(0);
      }, 500);
    }, SLIDE_DURATION);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const increment = 100 / (SLIDE_DURATION / 50);
        return prev + increment > 100 ? 100 : prev + increment;
      });
    }, 50);

    return () => {
      clearInterval(slideInterval);
      clearInterval(progressInterval);
    };
  }, [currentSlide]);

  const slide = slides[currentSlide];

  const featureLabels = ["Dashboard", "Goals", "Admin", "Members", "Catering", "Assets", "Community", "Integrations", "Library"];

  return (
    <div className="w-full max-w-5xl mx-auto mt-8 relative">
      {/* Sound toggle button */}
      <button
        onClick={toggleMute}
        className="absolute -top-10 right-0 z-30 p-2 rounded-full bg-muted/80 hover:bg-muted transition-colors"
        aria-label={isMuted ? "Unmute" : "Mute"}
        title={isMuted ? "Play background music" : "Mute"}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5 text-muted-foreground" />
        ) : (
          <Volume2 className="w-5 h-5 text-primary" />
        )}
      </button>

      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 shadow-2xl">
        {/* Main slide container */}
        <div
          className={`relative aspect-video transition-all duration-500 ease-out ${
            isTransitioning ? "opacity-0 scale-95" : "opacity-100 scale-100"
          }`}
        >
          {/* Background image or logo */}
          {slide.isLogo ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/20">
              <img
                src={slide.image}
                alt="PTA Hub"
                className="h-24 w-auto animate-pulse"
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted/50 to-muted">
              <img
                src={slide.image}
                alt={slide.headline}
                className="w-full h-full object-contain"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
            </div>
          )}

          {/* Text overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <h2
              className={`text-2xl md:text-4xl font-bold text-white mb-2 transition-all duration-500 ${
                isTransitioning
                  ? "translate-y-4 opacity-0"
                  : "translate-y-0 opacity-100"
              }`}
              style={{ transitionDelay: "100ms" }}
            >
              {slide.headline}
            </h2>
            <p
              className={`text-base md:text-xl text-white/90 transition-all duration-500 ${
                isTransitioning
                  ? "translate-y-4 opacity-0"
                  : "translate-y-0 opacity-100"
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              {slide.subtext}
            </p>
          </div>
        </div>

        {/* Progress indicators */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Dot indicators */}
          <div className="flex justify-center gap-1.5 mb-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentSlide(index);
                    setIsTransitioning(false);
                    setProgress(0);
                  }, 300);
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-white w-6"
                    : "bg-white/40 hover:bg-white/60 w-2"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white/80 transition-all duration-50 ease-linear rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Feature highlights below */}
      <div className="flex flex-wrap justify-center gap-2 mt-6">
        {featureLabels.map((feature, index) => (
          <div
            key={feature}
            className={`text-center px-3 py-1.5 rounded-lg bg-primary/5 transition-all duration-300 ${
              currentSlide === index
                ? "ring-2 ring-primary/50 scale-105 bg-primary/15"
                : ""
            }`}
          >
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              {feature}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MaintenanceShowcase;
